import { AnnualEmissionsByContext } from '@/types';
import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTheme } from 'styled-components';
import { Content, Stack, Inline, InlineItem } from '@kinesis/bungle';
//import { TextBlock } from '@/components/text-block';
import { Title } from '@/components/title';
import { renderValue, renderUnit } from '@/data/units';
import { renderVisualisationTooltip } from '@/components/visualisation-tooltip';
import { AnnualEmissionsLegend } from './annual-emissions-legend';
import { cloneDeep } from 'lodash/fp';
import { contextColors as seriesColors } from '@/data/colors';

const AnnualEmissions = (props: AnnualEmissionsProps) => {
  const theme = useTheme() as any;
  const { emissions: emissionsProp } = props;

  const options = useMemo(() => {
    const emissions = cloneDeep(emissionsProp);
    return {
      plotOptions: {
        series: {
          animation: false,
          pointStart: emissions.base,
          pointPlacement: 'on',
        },
        line: {
          marker: {
            enabled: false,
            symbol: 'circle',
            states: {
              hover: {
                radius: 4,
                lineWidth: 0,
              },
            },
          },
          states: {
            hover: {
              enabled: true,
              halo: null,
              lineWidthPlus: 0,
            },
          },
        },
      },
      legend: {
        enabled: false,
      },
      xAxis: {
        crosshair: {
          color: theme.color.gray3,
          zIndex: -1,
        },
        categories: emissions.current.map((_, index) => emissions.base + index),
        gridLineWidth: 1,
        gridLineColor: theme.colors.gray4.hex,
        lineColor: theme.colors.gray5.hex,
        lineWidth: 1,
        title: {
          style: {
            color: 'rgba(0,0,0,0.85)',
            fontWeight: 500,
          },
          y: 8, // move title to edge
        },
        tickInterval: 10,
        tickColor: theme.colors.gray5.hex,
        labels: {
          useHTML: true,
          formatter(o: any) {
            return `${o.value}`;
          },
          style: {
            color: 'rgba(0,0,0,0.65)',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '20px',
          },
          rotation: 0,
        },
      },
      yAxis: {
        min: 0.0,
        gridLineWidth: 1,
        gridLineColor: theme.colors.gray4.hex,
        lineColor: theme.colors.gray5.hex,
        lineWidth: 1,
        endOnTick: false,
        labels: {
          x: -9,
          useHTML: true,
          formatter(o: any) {
            return renderValue(o.value, emissions.unit);
          },
          style: {
            color: 'rgba(0,0,0,0.65)',
            fontSize: '12px',
            lineHeight: '20px',
            fontWeight: 400,
          },
        },
        title: {
          useHTML: true,
          text: `Annual emissions (${renderUnit(emissions.unit)})`,
          style: {
            color: 'rgba(0,0,0,0.85)',
            fontWeight: 500,
            fontSize: '12px',
            lineHeight: '20px',
          },
          offset: 106,
        },
      },
      credits: { enabled: false },
      title: { text: undefined },
      lang: {
        noData: 'There is no data.',
      },
      noData: {
        style: {
          fontWeight: 400,
          fontSize: '12px',
          lineHeight: '20px',
          color: 'rgba(0,0,0,0.25)',
        },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: 'rgba(255,255,255,0)',
        borderWidth: 0,
        shadow: false,
        animation: false,
        hideDelay: 0,
        outside: true,
        padding: 0,
        formatter(): any {
          const o = this as any;
          return renderVisualisationTooltip({
            title: `${o.x}`,
            entries: o.points.map((point: any) => ({
              color: point.series.color,
              label: point.series.name,
              value: point.y,
              unit: emissions.unit,
            })),
          });
        },
      },
      chart: {
        height: 338,
        marginLeft: 120,
        type: 'line',
        animation: false,
        plotBorderWidth: 1,
        plotBorderColor: theme.colors.gray4.hex,
        plotBackgroundColor: `rgba(0,0,0,0)`,
        spacing: [1, 1, 0, 1],
        style: {
          // eslint-disable-next-line max-len
          fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`,
          fontSize: '12px',
        },
      },
      series: [
        {
          color: seriesColors.current,
          name: 'Current settings',
          data: emissions.current,
        },
        {
          color: seriesColors.intermediate,
          name: 'Intermediate',
          data: emissions.intermediate,
        },
        {
          color: seriesColors.aggressive,
          name: 'Aggressive',
          data: emissions.aggressive,
        },
      ],
    };
  }, [theme, emissionsProp]);
  return (
    <Stack space='medium'>
      <Title>Annual pathway emissions</Title>
      {/* <TextBlock>Chart description TBC..</TextBlock>*/}
      <Content
        background='white'
        padding='medium'
        border
        paddingMode='equal'
        borderRadiusSize='small'
      >
        <Stack space='medium'>
          <Inline alignY='top'>
            <InlineItem sizing='fill-container'>
              <Content>
                <HighchartsReact highcharts={Highcharts} options={options} />
              </Content>
            </InlineItem>
            <InlineItem sizing='fit-content'>
              <AnnualEmissionsLegend />
            </InlineItem>
          </Inline>
        </Stack>
      </Content>
    </Stack>
  );
};

export type AnnualEmissionsProps = {
  emissions: AnnualEmissionsByContext;
};

export { AnnualEmissions };
